import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import {
  fetchCaeActionPlanAging,
  fetchCaeAuditorReliance,
  fetchCaeControlsByDept,
  fetchCaeControlUniverse,
  fetchCaeCoverageMatrix,
  fetchCaeExceptionsSummary,
  fetchCaeIssuesPipeline,
  fetchCaeITGCCoverage,
  fetchCaeMRC,
  fetchCaeTestingStatus,
  fetchCaeTesterWorkload,
  type CAEActionPlanAgingRow,
  type CAEAuditorRelianceRow,
  type CAEControlsByDeptRow,
  type CAEControlUniverseRow,
  type CAECoverageMatrixRow,
  type CAEExceptionsSummaryRow,
  type CAEIssuesPipelineRow,
  type CAEITGCCoverageRow,
  type CAEMRCRow,
  type CAETestingStatusRow,
  type CAETesterWorkloadRow,
} from '../../request/analytics/caeReports.ts';
import { analyticsCaeQueryKeys } from '../../queries/analytics/keys.ts';
import { useAnalyticsScope } from './useAnalyticsScope.tsx';
import { scopeKey } from '../../request/analytics/scope.ts';

/**
 * React Query v5 hooks for the CAE/Manager-persona analytics dashboard
 * (Feature 5.3, reports #9-#19). Each report is its own independent query
 * so a single failing/slow widget doesn't block the rest of the dashboard.
 */

export function useCaeControlUniverse(): UseQueryResult<CAEControlUniverseRow[], Error> {
  const scope = useAnalyticsScope();
  return useQuery({
    queryKey: analyticsCaeQueryKeys.controlUniverse(scopeKey(scope)),
    queryFn: () => fetchCaeControlUniverse(scope),
  });
}

export function useCaeTestingStatus(): UseQueryResult<CAETestingStatusRow[], Error> {
  const scope = useAnalyticsScope();
  return useQuery({
    queryKey: analyticsCaeQueryKeys.testingStatus(scopeKey(scope)),
    queryFn: () => fetchCaeTestingStatus(scope),
  });
}

export function useCaeIssuesPipeline(): UseQueryResult<CAEIssuesPipelineRow[], Error> {
  const scope = useAnalyticsScope();
  return useQuery({
    queryKey: analyticsCaeQueryKeys.issuesPipeline(scopeKey(scope)),
    queryFn: () => fetchCaeIssuesPipeline(scope),
  });
}

export function useCaeCoverageMatrix(): UseQueryResult<CAECoverageMatrixRow[], Error> {
  const scope = useAnalyticsScope();
  return useQuery({
    queryKey: analyticsCaeQueryKeys.coverageMatrix(scopeKey(scope)),
    queryFn: () => fetchCaeCoverageMatrix(scope),
  });
}

export function useCaeMRC(): UseQueryResult<CAEMRCRow[], Error> {
  const scope = useAnalyticsScope();
  return useQuery({
    queryKey: analyticsCaeQueryKeys.mrc(scopeKey(scope)),
    queryFn: () => fetchCaeMRC(scope),
  });
}

export function useCaeExceptionsSummary(): UseQueryResult<CAEExceptionsSummaryRow[], Error> {
  const scope = useAnalyticsScope();
  return useQuery({
    queryKey: analyticsCaeQueryKeys.exceptionsSummary(scopeKey(scope)),
    queryFn: () => fetchCaeExceptionsSummary(scope),
  });
}

export function useCaeAuditorReliance(): UseQueryResult<CAEAuditorRelianceRow[], Error> {
  const scope = useAnalyticsScope();
  return useQuery({
    queryKey: analyticsCaeQueryKeys.auditorReliance(scopeKey(scope)),
    queryFn: () => fetchCaeAuditorReliance(scope),
  });
}

export function useCaeActionPlanAging(): UseQueryResult<CAEActionPlanAgingRow[], Error> {
  const scope = useAnalyticsScope();
  return useQuery({
    queryKey: analyticsCaeQueryKeys.actionPlanAging(scopeKey(scope)),
    queryFn: () => fetchCaeActionPlanAging(scope),
  });
}

export function useCaeControlsByDept(): UseQueryResult<CAEControlsByDeptRow[], Error> {
  const scope = useAnalyticsScope();
  return useQuery({
    queryKey: analyticsCaeQueryKeys.controlsByDept(scopeKey(scope)),
    queryFn: () => fetchCaeControlsByDept(scope),
  });
}

export function useCaeITGCCoverage(): UseQueryResult<CAEITGCCoverageRow[], Error> {
  const scope = useAnalyticsScope();
  return useQuery({
    queryKey: analyticsCaeQueryKeys.itgcCoverage(scopeKey(scope)),
    queryFn: () => fetchCaeITGCCoverage(scope),
  });
}

export function useCaeTesterWorkload(): UseQueryResult<CAETesterWorkloadRow[], Error> {
  const scope = useAnalyticsScope();
  return useQuery({
    queryKey: analyticsCaeQueryKeys.testerWorkload(scopeKey(scope)),
    queryFn: () => fetchCaeTesterWorkload(scope),
  });
}
